import React, { useMemo } from 'react';
import { SwipeableCard } from '../../components';
import './conversation-card.scss';
import {
  ConversationData,
  ConversationStatusGroup,
  dispatch,
  getIsFieldOps,
  updateCurrentConversation,
  updateQueryThread,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { formatRelativeTime } from '@zonofi/common/functions/datetime-operations';
import { SVGS } from '../../assets';
import { camelCase, startCase } from 'lodash';
import { OptionButtonsProps, SwipeOptionButtons } from './swipe-option-buttons';
import { ReactComponent as ThreeDot } from '@zonofi/svg-icons/three-dot-horizontal.svg';
import { ReactComponent as UserFilled } from '@zonofi/svg-icons/user-filled.svg';
import { titleTruncate } from '@zonofi/common/functions/title-truncate';
import { useNavigate, useParams } from 'react-router-dom';
import { FIELD_OPS_ROUTES, SELLER_WEBAPP_ROUTES } from '../../constants';
import { StatusClassMap } from '../hooks/get-conversation-list';
import { TagsIcon, TagsKey } from '../contstants';
import { FunnelGroupInfo } from '../../types';

interface LifecycleEntry {
  path: string;
  info: FunnelGroupInfo | undefined;
}

const normalizeGroupName = (s: string) =>
  s.toLowerCase().replace(/[\s_-]/g, '');

const resolveLifecycleInfo = (
  funnelGroupMap: Record<string, FunnelGroupInfo>,
  path: string
): FunnelGroupInfo | undefined => {
  const normalizedPath = normalizeGroupName(path);
  return (
    funnelGroupMap[normalizedPath] ||
    funnelGroupMap[path] ||
    funnelGroupMap[path.toLowerCase()]
  );
};

interface ConversationCardProps extends ConversationData {
  onAssignTeamMember: () => void;
  onMoreAction: () => void;
  onChangeLifecycle: () => void;
  findStatusCategory: (status: string) => ConversationStatusGroup;
  findTagType: (tag: string) => TagsKey;
  funnelGroupMap?: Record<string, FunnelGroupInfo>;
}

export const ConversationCard: React.FC<ConversationCardProps> = ({
  onAssignTeamMember,
  onMoreAction,
  onChangeLifecycle,
  findStatusCategory,
  findTagType,
  funnelGroupMap = {},
  ...item
}) => {
  const {
    status = '',
    windowEndTime,
    metadata,
    conversationThreadId,
    customerId,
    chatId,
    isWidgetChat,
    customerLifeCyclePaths = [],
    channelName,
    title,
    description,
    assignedTo: conversationAssignedTo,
    queryType,
    channelId,
  } = item;

  const { tags = [], tag = [], campaignName } = metadata ?? {};
  const assignedTo = conversationAssignedTo ?? [];
  const displayCustomerName = channelName ?? '';
  const displayLastMessage = title || description || '';
  const normalizedTags = Array.isArray(tags)
    ? tags
    : Array.isArray(tag)
      ? tag
      : [];

  const navigate = useNavigate();
  const { customerId: customerIdParam } = useParams();
  const isFieldOps = useAppSelector(getIsFieldOps);

  const islockedWidgetChat = useMemo(
    () => (isWidgetChat && chatId === customerId) || !channelId,
    [isWidgetChat, chatId, customerId]
  );

  const options: OptionButtonsProps[] = [
    {
      background: '#8C8C8C',
      icon: <ThreeDot color="#8C8C8C" height={24} width={24} />,
      onPress: onMoreAction,
      title: 'More',
    },
    ...(!isFieldOps
      ? [
          {
            background: '#8C3CFF',
            icon: <UserFilled color="#8C3CFF" height={24} width={24} />,
            onPress: onAssignTeamMember,
            title: 'Assign Member',
          },
        ]
      : []),
  ];

  const statusClassName = StatusClassMap[findStatusCategory(status)];
  const MAX_VISIBLE_CHIPS = 3;

  // Pick only the latest lifecycle path (last in array)
  const latestGroup = useMemo<LifecycleEntry | null>(() => {
    if (!customerLifeCyclePaths.length) return null;
    const latestPath =
      customerLifeCyclePaths[customerLifeCyclePaths.length - 1];
    const info = resolveLifecycleInfo(funnelGroupMap, latestPath);
    return { path: latestPath, info };
  }, [customerLifeCyclePaths, funnelGroupMap]);

  const isLatestManual = latestGroup?.info?.group_type !== 'automatic';

  // Build unified chip list: [lifecycle chip?, ...tag chips]
  const allChips = useMemo<
    { key: string; label: string; element: JSX.Element }[]
  >(() => {
    const chips: { key: string; label: string; element: JSX.Element }[] = [];
    if (queryType) {
      chips.push({
        key: `query-type`,
        label: queryType,
        element: (
          <div
            className={`conversation-tags-each conversation-tags-query-type font-semibold`}
            key={`query-type`}
          >
            {queryType}
          </div>
        ),
      });
    }

    if (latestGroup) {
      const chipEl = isLatestManual ? (
        <div
          className="lifecycle-manual-chip font-semibold"
          onClick={(e) => {
            e.stopPropagation();
            onChangeLifecycle();
          }}
        >
          {latestGroup.info?.icon && (
            <span className="lifecycle-chip-icon">{latestGroup.info.icon}</span>
          )}
          <span>{startCase(camelCase(latestGroup.path))}</span>
          <img src={SVGS.ArrowDown} alt="" className="lifecycle-chip-chevron" />
        </div>
      ) : (
        <div
          className="lifecycle-auto-chip font-semibold"
          style={{ background: latestGroup.info?.color ?? '#595959' }}
        >
          {latestGroup.info?.icon && (
            <span className="lifecycle-chip-icon">{latestGroup.info.icon}</span>
          )}
          <span>{startCase(camelCase(latestGroup.path))}</span>
        </div>
      );
      chips.push({
        key: `lifecycle-${latestGroup.path}`,
        label: startCase(camelCase(latestGroup.path)),
        element: chipEl,
      });
    }

    normalizedTags.forEach((tag, idx) => {
      const tagKey = findTagType(tag);
      chips.push({
        key: `tag-${idx}-${tag}`,
        label: `${TagsIcon[tagKey]} ${startCase(camelCase(tag))}`,
        element: (
          <div
            className={`conversation-tags-each conversation-tags-${tagKey} font-semibold`}
            key={`tag-${idx}`}
          >
            {TagsIcon[tagKey]} {startCase(camelCase(tag))}
          </div>
        ),
      });
    });

    return chips;
  }, [latestGroup, isLatestManual, normalizedTags, findTagType]);

  const visibleChips = allChips.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenChips = allChips.slice(MAX_VISIBLE_CHIPS);
  const hasChips = allChips.length > 0;

  return (
    <SwipeableCard
      swipeableClassName="conversation-card-swipeable font-regular"
      className="conversation-card-container font-regular"
      rightButton={{
        element: <SwipeOptionButtons options={options} />,
        onPress: () => {},
        buttonWidth: 180,
      }}
      isSwipeDisabled={islockedWidgetChat}
    >
      <div
        className="conversation-card"
        onClick={() => {
          dispatch(updateCurrentConversation(item));
          dispatch(
            updateQueryThread({
              startMessageTimetoken: item?.startMessageTimetoken ?? '',
              title: item?.title ?? '',
              conversationId: item?.conversationThreadId ?? '',
              endMessageTimetoken: item?.endMessageTimetoken ?? '',
              customerIds: [item?.customerId ?? ''],
              channelId: item?.channelId ?? '',
              channelName: item?.channelName ?? '',
              queryType: item?.queryType ?? '',
              workspaceId: item?.workspaceId,
              backgroundColor: '#7f2cf4',
            })
          );
          if (!islockedWidgetChat) {
            if (isFieldOps) {
              navigate(
                `${FIELD_OPS_ROUTES.ISSUE_DETAILS}/${conversationThreadId ?? ''}`?.replace(
                  ':customerId',
                  customerId ?? ''
                )
              );
            } else {
              navigate(
                `${SELLER_WEBAPP_ROUTES.CONVERSATION_MESSAGES.replace(':conversationId', conversationThreadId ?? '')}?customerId=${customerId}`
              );
            }
          }
        }}
      >
        {/* Row 1: Status + assignee + time */}
        <div className="conversation-metadata font-semibold">
          <div className="conversation-status">
            <div className={`status-details ${statusClassName}`}>
              <div className={`marker ${statusClassName}`} />
              {startCase(status)}
            </div>
            {islockedWidgetChat && (
              <img src={SVGS.LockIcon} height={12} width={12} />
            )}
            {assignedTo?.length > 0 ? (
              <div className="assignee-details">
                <UserFilled color="#249F6C" />
                {`${titleTruncate(assignedTo?.[0]?.teamMemberName, 20)}${
                  assignedTo.length - 1 > 0 ? ` + ${assignedTo.length - 1}` : ''
                }`}
              </div>
            ) : (
              <div className="not-assigned-details">
                <UserFilled color="#DD360C" />
                Not Assigned
              </div>
            )}
          </div>
          <div className="conversation-time">
            <img src={SVGS.Clock} alt="clock" height={16} width={16} />
            {windowEndTime ? formatRelativeTime(new Date(windowEndTime)) : ''}
          </div>
        </div>

        {/* Row 2+3: Customer info block */}
        <div className="conversation-customer">
          {/* Campaign source */}
          <div className="campaign-source font-semibold">
            {campaignName && (
              <img
                src={SVGS.CampaignIcon}
                alt="campaign"
                height={12}
                width={12}
              />
            )}
            {campaignName && <span>Campaign Source: {campaignName}</span>}
          </div>

          {/* Customer name */}
          {displayCustomerName && (
            <div className="conversation-customer-details font-semibold">
              {displayCustomerName}
            </div>
          )}

          {/* Last message / summary */}
          <div className="conversation-customer-last-message font-regular conversation-last-message-clamp">
            {displayLastMessage}
          </div>
        </div>

        {/* Row 4: Lifecycle chip (latest only) + tags, max 3 visible */}
        {hasChips && (
          <div className="conversation-tags-row">
            {visibleChips.map(({ key, element }) => (
              <React.Fragment key={key}>{element}</React.Fragment>
            ))}
            {hiddenChips.length > 0 && (
              <div className="tags-overflow-chip font-semibold">
                +{hiddenChips.length}
                <div className="tags-overflow-tooltip">
                  {hiddenChips.map(({ key, label }) => (
                    <span key={key} className="tooltip-item">
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </SwipeableCard>
  );
};
